/*
 * Wallet: the balance, withdrawals and the bank account they are paid to.
 *
 *   commission earned ──30-day hold──▶ available ──request──▶ in process
 *                                      (Withdraw)             requested → approved → paid (UTR)
 *                                                             or rejected (amount returns to available)
 *                                                             or cancelled by the partner while requested
 *
 * A partner who is also a Wealthoak client can instead pay an invoice on their
 * own subscription from the wallet (OwnInvoicesCard). That set-off is listed
 * with the withdrawals: processing → applied (Zoho payment number), or
 * returned to the wallet if it never reached the invoice.
 */

import { useState } from "react";
import { errorMessage, wallet as walletApi, type PayoutAccountMasked, type Wallet, type Withdrawal } from "../api/client";
import { useLoad } from "../api/useLoad";
import { useMe } from "../auth/AuthContext";
import AutoPayoutCard from "./AutoPayoutCard";
import BankAccountCard from "./BankAccountCard";
import { EmptyState, LoadError, SETOFF_STATUS, SkeletonRows, Status, WITHDRAWAL_STATUS, type Notify } from "./bits";
import { fmtDate, fmtDateTime, inr, toPaise } from "./format";
import { ArrowRight, Info, Wallet as WalletIcon } from "./icons";
import OwnInvoicesCard from "./OwnInvoicesCard";
import type { View } from "./Shell";
import WithdrawDialog, { bankLine } from "./WithdrawDialog";
import "./wallet.css";

export default function WalletWorkspace({ notify, onNavigate }: { notify: Notify; onNavigate: (view: View) => void }) {
  const me = useMe();
  const w = useLoad(() => walletApi.get());
  const history = useLoad(() => walletApi.withdrawals());
  const [withdrawing, setWithdrawing] = useState(false);

  const data = w.data;
  const blocked: string | null = !data
    ? "Loading…"
    : me.status !== "active"
      ? "Withdrawals open once Wealthoak approves your partner account."
      : !data.payout_account
        ? "Add your bank account below before withdrawing."
        : toPaise(data.available) <= 0
          ? data.next_release
            ? `Nothing to withdraw yet. ${inr(data.next_release.amount)} becomes available on ${fmtDate(data.next_release.date)}.`
            : `Nothing to withdraw yet. Commission becomes withdrawable ${data.hold_days} days after your client pays.`
          : null;

  function refresh() {
    w.reload();
    history.reload();
  }

  return (
    <>
      <section className="page-heading">
        <div>
          <p className="eyebrow">Wallet</p>
          <h1>Your commission wallet</h1>
          <p className="page-heading__sub">
            Commission is held for {data?.hold_days ?? me.hold_days} days after your client pays, then it&apos;s yours
            to withdraw. Wealthoak&apos;s finance team pays each request to your bank, or turn on auto payout to be paid
            every month without asking.
          </p>
        </div>
      </section>

      {w.error && !data ? (
        <LoadError message={w.error} onRetry={refresh} />
      ) : (
        <section className="card wallet-hero" aria-label="Balance">
          <div className="wallet-hero__main">
            <p className="eyebrow">Available to withdraw</p>
            {data ? (
              <strong className="wallet-hero__value num">{inr(data.available)}</strong>
            ) : (
              <span className="skel skel--xl" style={{ width: 220, marginTop: 12 }} />
            )}
            <div className="wallet-hero__action">
              <button
                className="button button--primary"
                disabled={Boolean(blocked)}
                aria-describedby={blocked && data ? "wd-blocked" : undefined}
                onClick={() => setWithdrawing(true)}
              >
                <WalletIcon size={16} /> Withdraw
              </button>
              {blocked && data && (
                <p id="wd-blocked" className="wallet-hero__why">
                  <Info size={14} />
                  {blocked}
                </p>
              )}
            </div>
          </div>
          <dl className="wallet-hero__side">
            <Figure
              label="Pending"
              value={data?.pending}
              sub={
                data?.next_release
                  ? `${inr(data.next_release.amount)} releases on ${fmtDate(data.next_release.date)}`
                  : "Nothing inside the hold"
              }
            />
            <Figure label="In process" value={data?.in_process} sub="Requested or approved, not yet paid" />
            <Figure label="Withdrawn" value={data?.withdrawn} sub="Paid to your bank, lifetime" />
            {data && toPaise(data.set_off) > 0 && (
              <Figure label="Set off" value={data.set_off} sub="Paid on your own Wealthoak invoices" />
            )}
            <Figure label="Lifetime earned" value={data?.lifetime} sub="All commission accrued" />
          </dl>
        </section>
      )}

      <AutoPayoutCard wallet={data} notify={notify} />

      <OwnInvoicesCard wallet={data} notify={notify} onPaid={refresh} />

      <div className="wallet-grid">
        <section className="card card--pad" aria-labelledby="wd-title" aria-busy={history.loading}>
          <div className="card-heading">
            <div>
              <p className="eyebrow">History</p>
              <h2 id="wd-title">Withdrawals</h2>
            </div>
            <button className="text-button" onClick={() => onNavigate("transactions")}>
              All transactions <ArrowRight size={14} />
            </button>
          </div>
          {history.error ? (
            <div style={{ marginTop: 14 }}>
              <LoadError message={history.error} onRetry={history.reload} />
            </div>
          ) : !history.data ? (
            <SkeletonRows rows={3} label="Loading withdrawals" />
          ) : history.data.items.length === 0 ? (
            <EmptyState icon={ArrowRight} title="No withdrawals yet">
              When you withdraw, each request shows here as it moves from requested to approved to paid.
            </EmptyState>
          ) : (
            <ul className="wd-list">
              {history.data.items.map((item) => (
                <WithdrawalItem key={item.id} w={item} notify={notify} onChanged={refresh} />
              ))}
            </ul>
          )}
        </section>

        {data || !w.error ? (
          data ? (
            <BankAccountCard
              key={data.payout_account?.updated_at ?? "none"}
              account={data.payout_account}
              partnerName={me.full_name}
              notify={notify}
              onSaved={(a: PayoutAccountMasked) => w.setData({ ...data, payout_account: a })}
            />
          ) : (
            <section className="card card--pad">
              <SkeletonRows rows={3} label="Loading bank account" />
            </section>
          )
        ) : null}
      </div>

      {withdrawing && data?.payout_account && (
        <WithdrawDialog
          wallet={data as Wallet & { payout_account: PayoutAccountMasked }}
          onClose={() => setWithdrawing(false)}
          onDone={(created) => {
            setWithdrawing(false);
            notify(`Withdrawal of ${inr(created.amount)} requested. We'll let you know once it's paid.`);
            refresh();
          }}
        />
      )}
    </>
  );
}

function Figure({ label, value, sub }: { label: string; value: string | undefined; sub: string }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>
        {value !== undefined ? <strong className="num">{inr(value)}</strong> : <span className="skel" style={{ width: 90, marginTop: 6 }} />}
        <small>{value !== undefined ? sub : " "}</small>
      </dd>
    </div>
  );
}

/* ---------- one withdrawal, with its timeline ---------- */

type Step = { key: string; label: string; at: string | null; state: "done" | "now" | "todo" | "bad"; note?: string };

/** A set-off: paid from the wallet, then applied to the invoice in Wealthoak's billing. */
function setoffStepsFor(w: Withdrawal): Step[] {
  const taken: Step = { key: "requested", label: "Paid from wallet", at: w.requested_at, state: "done" };
  if (w.status === "paid") {
    return [
      taken,
      {
        key: "paid",
        label: "Applied to invoice",
        at: w.paid_at,
        state: "done",
        note: w.payout_reference ? `Payment ${w.payout_reference}` : undefined,
      },
    ];
  }
  if (w.status === "rejected" || w.status === "cancelled") {
    return [
      taken,
      { key: "rejected", label: "Returned to wallet", at: w.decided_at, state: "bad", note: w.reject_reason ?? undefined },
    ];
  }
  return [{ ...taken, state: "now" }, { key: "paid", label: "Applied to invoice", at: null, state: "todo" }];
}

/** A monthly auto payout: no finance approval, so it goes straight from started to paid (or back to the wallet). */
function autoStepsFor(w: Withdrawal): Step[] {
  const started: Step = { key: "requested", label: "Auto payout started", at: w.requested_at, state: "done" };
  if (w.status === "paid") {
    return [
      started,
      {
        key: "paid",
        label: "Paid",
        at: w.paid_at,
        state: "done",
        note: w.payout_reference ? `UTR ${w.payout_reference.replace(/^UTR\s*/i, "")}` : undefined,
      },
    ];
  }
  if (w.status === "rejected" || w.status === "cancelled") {
    return [
      started,
      { key: "rejected", label: "Returned to wallet", at: w.decided_at, state: "bad", note: w.reject_reason ?? undefined },
    ];
  }
  return [{ ...started, state: "now" }, { key: "paid", label: "Paid", at: null, state: "todo" }];
}

function stepsFor(w: Withdrawal): Step[] {
  if (w.method === "subscription") return setoffStepsFor(w);
  if (w.source === "auto") return autoStepsFor(w);
  const requested: Step = { key: "requested", label: "Requested", at: w.requested_at, state: "done" };
  switch (w.status) {
    case "requested":
      return [
        { ...requested, state: "now" },
        { key: "approved", label: "Approved", at: null, state: "todo" },
        { key: "paid", label: "Paid", at: null, state: "todo" },
      ];
    case "approved":
      return [
        requested,
        { key: "approved", label: "Approved", at: w.decided_at, state: "now" },
        { key: "paid", label: "Paid", at: null, state: "todo" },
      ];
    case "paid":
      return [
        requested,
        { key: "approved", label: "Approved", at: w.decided_at, state: "done" },
        {
          key: "paid",
          label: "Paid",
          at: w.paid_at,
          state: "done",
          note: w.payout_reference ? `UTR ${w.payout_reference.replace(/^UTR\s*/i, "")}` : undefined,
        },
      ];
    case "rejected":
      return [
        requested,
        { key: "rejected", label: "Rejected", at: w.decided_at, state: "bad", note: w.reject_reason ?? undefined },
      ];
    case "cancelled":
      return [requested, { key: "cancelled", label: "Cancelled by you", at: w.decided_at, state: "bad" }];
  }
}

function WithdrawalItem({ w, notify, onChanged }: { w: Withdrawal; notify: Notify; onChanged: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const setoff = w.method === "subscription";
  const auto = w.source === "auto";
  const st = (setoff ? SETOFF_STATUS : WITHDRAWAL_STATUS)[w.status];

  async function cancel() {
    setBusy(true);
    setError(null);
    try {
      await walletApi.cancel(w.id);
      notify(`Withdrawal of ${inr(w.amount)} cancelled. The amount is back in your available balance.`);
      onChanged();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <li className={`wd-item is-${w.status}`}>
      <div className="wd-item__top">
        <span>
          <strong className="num">
            {inr(w.net_amount)}
            {auto && <span className="wd-auto">Auto payout</span>}
          </strong>
          <small className="num">
            {inr(w.amount)} − GST {inr(w.gst_amount)} − TDS {inr(w.tds_amount)}
          </small>
          {w.account ? (
            <small>
              To {w.account.account_holder} · {bankLine(w.account)}
            </small>
          ) : (
            <small>On your Wealthoak invoice {w.invoice_number}</small>
          )}
        </span>
        <Status tone={st.tone}>{st.label}</Status>
      </div>

      <ol className="wd-steps" aria-label={`Progress: ${st.label}`}>
        {stepsFor(w).map((s) => (
          <li key={s.key} className={`is-${s.state}`} aria-current={s.state === "now" ? "step" : undefined}>
            <i aria-hidden="true" />
            <span>
              <b>{s.label}</b>
              <small>{s.at ? fmtDateTime(s.at) : s.state === "todo" ? (setoff ? "In a minute" : "Waiting") : ""}</small>
              {s.note && <small className={s.key === "paid" ? "wd-utr mono" : "wd-reason"}>{s.note}</small>}
            </span>
          </li>
        ))}
      </ol>

      {w.status === "requested" && !setoff && !auto && (
        <div className="wd-item__actions">
          {confirming ? (
            <>
              <span>Cancel this withdrawal?</span>
              <button className="button button--secondary button--sm" onClick={() => setConfirming(false)} disabled={busy}>
                Keep it
              </button>
              <button className="button button--sm button--danger" onClick={() => void cancel()} disabled={busy}>
                {busy ? "Cancelling…" : "Yes, cancel"}
              </button>
            </>
          ) : (
            <button className="button button--secondary button--sm" onClick={() => setConfirming(true)}>
              Cancel request
            </button>
          )}
        </div>
      )}
      {error && <p className="field__error" role="alert">{error}</p>}
    </li>
  );
}
